// File validation utilities

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

// Allowed file types and their MIME types
export const ALLOWED_FILE_TYPES: Record<string, readonly string[]> = {
  // PDFs
  'application/pdf': ['.pdf'],
  
  // Microsoft Word
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  
  // Microsoft Excel
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  
  // CSV
  'text/csv': ['.csv'],
  'application/csv': ['.csv'],
  
  // Text files
  'text/plain': ['.txt'],
  
  // Images (for drawings/photos)
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/bmp': ['.bmp'],
  'image/webp': ['.webp'],
  
  // Additional common formats
  'application/rtf': ['.rtf'],
};

// Maximum file size in bytes (100 MB)
export const MAX_FILE_SIZE = 100 * 1024 * 1024;

// Get file extension from filename
export function getFileExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex > 0 ? filename.substring(lastDotIndex).toLowerCase() : '';
}

// Get allowed extensions as a flat array
export function getAllowedExtensions(): string[] {
  return Object.values(ALLOWED_FILE_TYPES).flat();
}

// Get allowed MIME types
export function getAllowedMimeTypes(): string[] {
  return Object.keys(ALLOWED_FILE_TYPES);
}

// Validate file type based on MIME type and extension
export function validateFileType(file: File): FileValidationResult {
  const extension = getFileExtension(file.name);
  const mimeType = file.type;
  
  // Check if MIME type is allowed
  const allowedMimeTypes = getAllowedMimeTypes();
  if (!allowedMimeTypes.includes(mimeType)) {
    // Fallback: check by extension if MIME type is not recognized
    const allowedExtensions = getAllowedExtensions();
    if (!allowedExtensions.includes(extension)) {
      return {
        isValid: false,
        error: `File type not supported. Allowed types: ${allowedExtensions.join(', ')}`
      };
    }
  }
  
  // Verify that the extension matches the MIME type
  if (mimeType && mimeType in ALLOWED_FILE_TYPES) {
    const expectedExtensions = ALLOWED_FILE_TYPES[mimeType];
    if (expectedExtensions && !expectedExtensions.includes(extension)) {
      return {
        isValid: false,
        error: `File extension "${extension}" doesn't match the detected file type. Expected: ${expectedExtensions.join(', ')}`
      };
    }
  }
  
  return { isValid: true };
}

// Validate file size
export function validateFileSize(file: File): FileValidationResult {
  if (file.size > MAX_FILE_SIZE) {
    const maxSizeMB = Math.round(MAX_FILE_SIZE / (1024 * 1024));
    const fileSizeMB = Math.round((file.size / (1024 * 1024)) * 100) / 100;
    return {
      isValid: false,
      error: `File size (${fileSizeMB} MB) exceeds maximum allowed size of ${maxSizeMB} MB`
    };
  }
  
  if (file.size === 0) {
    return {
      isValid: false,
      error: 'File is empty or corrupted'
    };
  }
  
  return { isValid: true };
}

// Comprehensive file validation
export function validateFile(file: File): FileValidationResult {
  // Check file size first
  const sizeValidation = validateFileSize(file);
  if (!sizeValidation.isValid) {
    return sizeValidation;
  }
  
  // Check file type
  const typeValidation = validateFileType(file);
  if (!typeValidation.isValid) {
    return typeValidation;
  }
  
  return { isValid: true };
}

// Format file size for display
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Get user-friendly description of allowed file types
export function getAllowedFileTypesDescription(): string {
  const extensions = getAllowedExtensions();
  const maxSizeMB = Math.round(MAX_FILE_SIZE / (1024 * 1024));
  return `Allowed: ${extensions.join(', ')} | Max size: ${maxSizeMB} MB`;
} 